import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import StartingBanner from '@/components/system/StartingBanner';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { loadBootstrap } from '@/lib/useBootstrap';
import { hasBdAccess } from '@/lib/roles';
import api from '@/lib/api';
import { clearAllSubmissionDrafts } from '@/lib/submissionDraft';
import { bootSplashAuthReady, holdBootSplash, releaseBootSplash } from '@/lib/bootSplash';
import { Layout } from '@/components/Layout';
import MarketingScope from '@/components/marketing/MarketingScope';
import AuthCallback from '@/components/AuthCallback';
import { BrandedLoader } from '@/components/BrandedLoader';
import { rootRedirectPath } from '@/config/brand';
import { TopLoadingBar } from '@/components/TopLoadingBar';
import { primeFromUrl } from '@/lib/candidateFull';

// Deep-linking straight to a candidate is a cold boot: the shell spends ~4s on
// /api/bootstrap before the profile route even loads. Fire the profile fetch
// here instead, at bundle-parse time, so it runs alongside the boot.
primeFromUrl();

// ── Lazy-loaded pages ─────────────────────────────────────────────────────────
// Auth Pages
const LoginPage           = lazy(() => import('@/pages/LoginPage'));
const SignupPage           = lazy(() => import('@/pages/SignupPage'));
const ForgotPasswordPage   = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage    = lazy(() => import('@/pages/ResetPasswordPage'));
const ControlSettingsPage  = lazy(() => import('@/pages/ControlSettingsPage'));
const ChangePasswordPage   = lazy(() => import('@/pages/ChangePasswordPage'));
const ProfilePage          = lazy(() => import('@/pages/ProfilePage'));
const OutreachPage         = lazy(() => import('@/pages/OutreachPage'));
const OwnerDashboardPage   = lazy(() => import('@/pages/OwnerDashboardPage'));
const WhatsNewPage         = lazy(() => import('@/pages/WhatsNewPage'));
const ScoringEnginePage    = lazy(() => import('@/pages/ScoringEnginePage'));

// ATS Pages
const DashboardPage        = lazy(() => import('@/pages/DashboardPage'));
const ScreeningQueuePage   = lazy(() => import('@/pages/ScreeningQueuePage'));
const NotificationsPage    = lazy(() => import('@/pages/NotificationsPage'));
const JobsPage             = lazy(() => import('@/pages/JobsPage'));
const PipelinePage         = lazy(() => import('@/pages/PipelinePage'));
const TalentHubPage        = lazy(() => import('@/pages/TalentHubPage'));
const SubmissionsPage      = lazy(() => import('@/pages/SubmissionsPage'));
const CandidateDetailPage  = lazy(() => import('@/pages/CandidateDetailPage'));
const ClientsPage          = lazy(() => import('@/pages/ClientsPage'));
const ClientDetailPage     = lazy(() => import('@/pages/ClientDetailPage'));
const InterviewsPage       = lazy(() => import('@/pages/InterviewsPage'));
const AnalyticsPage        = lazy(() => import('@/pages/AnalyticsPage'));
const ReportsPage          = lazy(() => import('@/pages/ReportsPage'));
const UserManagementPage   = lazy(() => import('@/pages/UserManagementPage'));
const SharedCandidatesPage = lazy(() => import('@/pages/SharedCandidatesPage'));
const ClientTrackerPublicPage = lazy(() => import('@/pages/ClientTrackerPublicPage'));
const ContentManagement    = lazy(() => import('@/pages/ContentManagement'));
const DailyTargetsGridPage = lazy(() => import('@/pages/DailyTargetsGridPage'));
const TrainingPage         = lazy(() => import('@/pages/TrainingPage'));
const JobResearchPage      = lazy(() => import('@/pages/JobResearchPage'));
const SeoControlPage       = lazy(() => import('@/pages/SeoControlPage'));
const ClientPortalAdminPage = lazy(() => import('@/pages/ClientPortalAdminPage'));
const SecurityCenterPage   = lazy(() => import('@/pages/SecurityCenterPage'));
const EmailLogsPage        = lazy(() => import('@/pages/EmailLogsPage'));
const AIRecruiterPage      = lazy(() => import('@/pages/AIRecruiterPage'));
const AiRecruitPage        = lazy(() => import('@/pages/airec/AiRecruitPage'));
const BizXPage             = lazy(() => import('@/pages/BizXPage'));
const AiCallCampaignsPage  = lazy(() => import('@/pages/AiCallCampaignsPage'));
const AiAutodialerPage     = lazy(() => import('@/pages/AiAutodialerPage'));
const PublicAiRecruiterPage = lazy(() => import('@/pages/PublicAiRecruiterPage'));
const HelpCenterPage       = lazy(() => import('@/pages/HelpCenterPage'));
const BulkImportPage       = lazy(() => import('@/pages/BulkImportPage'));
const BillingPage          = lazy(() => import('@/pages/BillingPage'));

// Combined pages (nav consolidation)
const CombinedReportsPage    = lazy(() => import('@/pages/CombinedReportsPage'));
const CombinedClientsPage    = lazy(() => import('@/pages/CombinedClientsPage'));
const MyClientsPage          = lazy(() => import('@/pages/MyClientsPage'));
const CombinedTeamPage       = lazy(() => import('@/pages/CombinedTeamPage'));
const CombinedLearningPage   = lazy(() => import('@/pages/CombinedLearningPage'));
const CombinedSettingsPage   = lazy(() => import('@/pages/CombinedSettingsPage'));
const CombinedAdminToolsPage = lazy(() => import('@/pages/CombinedAdminToolsPage'));
const CombinedCalendarPage   = lazy(() => import('@/pages/CombinedCalendarPage'));
const MeetingsPage           = lazy(() => import('@/pages/MeetingsPage'));
const HiroMatchPage          = lazy(() => import('@/pages/HiroMatchPage'));
const BusinessDevPage        = lazy(() => import('@/pages/BusinessDevPage'));
const BdDashboardPage        = lazy(() => import('@/pages/BdDashboardPage'));
const BdGuidePage            = lazy(() => import('@/pages/BdGuidePage'));
const AtsGuidePage           = lazy(() => import('@/pages/AtsGuidePage'));
const ProductAnalyticsPage   = lazy(() => import('@/pages/ProductAnalyticsPage'));
const SourcingActivityPage   = lazy(() => import('@/pages/SourcingActivityPage'));

// Marketing Pages
const HomePage            = lazy(() => import('@/pages/marketing/HomePage'));
const ServicesPage        = lazy(() => import('@/pages/marketing/ServicesPage'));
const AboutPage           = lazy(() => import('@/pages/marketing/AboutPage'));
const ContactPage         = lazy(() => import('@/pages/marketing/ContactPage'));
const BlogPage            = lazy(() => import('@/pages/marketing/BlogPage'));
const BlogDetailPage      = lazy(() => import('@/pages/marketing/BlogDetailPage'));
const CareersPage         = lazy(() => import('@/pages/marketing/CareersPage'));
const OpenPositionsPage   = lazy(() => import('@/pages/marketing/OpenPositionsPage'));
const RecruiterApplyPage  = lazy(() => import('@/pages/RecruiterApplyPage'));
const ResumeUploadPage    = lazy(() => import('@/pages/marketing/ResumeUploadPage'));
const AiScreenConfirmPage = lazy(() => import('@/pages/marketing/AiScreenConfirmPage'));
const AvailabilityPage = lazy(() => import('@/pages/public/AvailabilityPage'));
const BookingPage = lazy(() => import('@/pages/public/BookingPage'));
const BookingManagePage = lazy(() => import('@/pages/public/BookingManagePage'));
const CandidateSlotPage = lazy(() => import('@/pages/public/CandidateSlotPage'));
const InterviewFeedbackPage = lazy(() => import('@/pages/public/InterviewFeedbackPage'));
const InterviewConfirmPage = lazy(() => import('@/pages/public/InterviewConfirmPage'));
const JobDetailPage       = lazy(() => import('@/pages/marketing/JobDetailPage'));
const SalaryCalculatorPage = lazy(() => import('@/pages/marketing/SalaryCalculatorPage'));
const HireLandingPage     = lazy(() => import('@/pages/marketing/HireLandingPage'));
const MarketingAnalyticsPage = lazy(() => import('@/pages/MarketingAnalyticsPage'));
const FitmentFormPage     = lazy(() => import('@/pages/FitmentFormPage'));
const NotFoundPage        = lazy(() => import('@/pages/NotFoundPage'));

// DPDP / Compliance Pages (Phase 2 Security · agent/phase2-dpdp)
const PrivacyPolicyPage   = lazy(() => import('@/pages/marketing/PrivacyPolicyPage'));
const MyDataPage          = lazy(() => import('@/pages/marketing/MyDataPage'));
const DSARQueuePage       = lazy(() => import('@/pages/admin/DSARQueuePage'));
const CompliancePage      = lazy(() => import('@/pages/admin/CompliancePage'));
const SecretsHealthPage   = lazy(() => import('@/pages/admin/SecretsHealthPage'));
const InternalDataHandlingPage = lazy(() => import('@/pages/admin/InternalDataHandlingPage'));
const DataHygienePage     = lazy(() => import('@/pages/admin/DataHygienePage'));
const DataHealthPage      = lazy(() => import('@/pages/admin/DataHealthPage'));
const SalaryRepairPage    = lazy(() => import('@/pages/admin/SalaryRepairPage'));
const AdminShareGrantsPage = lazy(() => import('@/pages/AdminShareGrantsPage'));
const ComplianceHubPage   = lazy(() => import('@/pages/admin/ComplianceHubPage'));
const BDOutreachHealthPage = lazy(() => import('@/pages/admin/BDOutreachHealthPage'));
const RegressionHealthPage = lazy(() => import('@/pages/admin/RegressionHealthPage'));
const ContactRevealAdminPage = lazy(() => import('@/pages/admin/ContactRevealAdminPage'));
const EmailRoutingPage = lazy(() => import('@/pages/admin/EmailRoutingPage'));
const AdminOnlyPage = lazy(() => import('@/pages/admin/AdminOnlyPage'));
const SearchActivityPage = lazy(() => import('@/pages/admin/SearchActivityPage'));
const QueueHealthPage    = lazy(() => import('@/pages/QueueHealthPage'));
const ControlCentrePage  = lazy(() => import('@/pages/ControlCentrePage'));
const ApiUsagePage = lazy(() => import('@/pages/admin/ApiUsagePage'));
const ApiUsageSharedPage = lazy(() => import('@/pages/admin/ApiUsageSharedPage'));

// Client Portal Pages

// Client ATS — Platform Console (superadmin, multi-tenant) + invite accept
const PlatformWorkspacesPage      = lazy(() => import('@/pages/platform/PlatformWorkspacesPage'));
const PlatformWorkspaceDetailPage = lazy(() => import('@/pages/platform/PlatformWorkspaceDetailPage'));
const PlatformUsagePage           = lazy(() => import('@/pages/platform/PlatformUsagePage'));
const PlatformAuditPage           = lazy(() => import('@/pages/platform/PlatformAuditPage'));
const PlatformHirobaseLeadsPage   = lazy(() => import('@/pages/platform/PlatformHirobaseLeadsPage'));
const WorkspaceInviteAcceptPage   = lazy(() => import('@/pages/WorkspaceInviteAcceptPage'));

// Client ATS — Workspace (tenant) app (Phase 2)
const WorkspaceLayout             = lazy(() => import('@/components/WorkspaceLayout'));
const WsClientLoginPage           = lazy(() => import('@/pages/workspace/ClientLoginPage'));
const WsWorkspaceHome             = lazy(() => import('@/pages/workspace/WorkspaceHome'));
const WsJobsPage                  = lazy(() => import('@/pages/workspace/ClientJobsPage'));
const WsJobBoardPage              = lazy(() => import('@/pages/workspace/ClientJobBoardPage'));
const WsScorecardTemplatePage     = lazy(() => import('@/pages/workspace/ScorecardTemplatePage'));
const WsCandidatesPage            = lazy(() => import('@/pages/workspace/ClientCandidatesPage'));
const WsCandidateProfilePage      = lazy(() => import('@/pages/workspace/ClientCandidateProfilePage'));
const WsInterviewsPage            = lazy(() => import('@/pages/workspace/ClientInterviewsPage'));
const WsMyInterviewsPage          = lazy(() => import('@/pages/workspace/MyInterviewsPage'));
const WsScorecardFillPage         = lazy(() => import('@/pages/workspace/ScorecardFillPage'));
const WsMembersPage               = lazy(() => import('@/pages/workspace/WorkspaceMembersPage'));
const WsUsagePage                 = lazy(() => import('@/pages/workspace/WorkspaceUsagePage'));
const WsAuditPage                 = lazy(() => import('@/pages/workspace/WorkspaceAuditPage'));
const WsSettingsPage              = lazy(() => import('@/pages/workspace/WorkspaceSettingsPage'));
const WsAgenciesPage              = lazy(() => import('@/pages/workspace/ClientAgenciesPage'));
const WsAgencyJobsPage            = lazy(() => import('@/pages/workspace/AgencySharedJobsPage'));
const WsAgencySubmissionsPage     = lazy(() => import('@/pages/workspace/AgencySubmissionsPage'));
const WsAgencyPerformancePage     = lazy(() => import('@/pages/workspace/AgencyPerformancePage'));
const WsPublicSchedulePage        = lazy(() => import('@/pages/workspace/PublicSchedulePage'));
const WsForgotPasswordPage        = lazy(() => import('@/pages/workspace/ClientForgotPasswordPage'));
const WsResetPasswordPage         = lazy(() => import('@/pages/workspace/ClientResetPasswordPage'));
const HirobaseMarketingPage       = lazy(() => import('@/pages/marketing/HirobasePage'));

// Page loader — branded (HiroJet). Client/Hirobase routes get a neutral bone
// screen instead so the internal brand never flashes on client surfaces.
const PageLoader = () => {
  const isClientRoute = /^\/(w\/|workspace-|hirobase|client\/login|schedule\/)/.test(window.location.pathname);
  // On first boot the HTML curtain is already covering the screen — drawing a
  // second loader here is what made the mark jump. Hold the curtain instead.
  const bootUp = useRef(!!document.getElementById('splash-screen')).current;
  useEffect(() => {
    if (!bootUp) return undefined;
    holdBootSplash();
    return releaseBootSplash;
  }, [bootUp]);
  if (bootUp) return null;
  if (isClientRoute) return <div className="hb-scope min-h-screen bg-surface-0" />;
  return <BrandedLoader variant="page" />;
};

// Sitemap redirect — /sitemap.xml → /api/seo/sitemap.xml
const SitemapRedirect = () => { window.location.replace('/api/seo/sitemap.xml'); return null; };

// Root ("/") landing — a mapped workspace/app host (e.g. app.hirobase.com) lands
// straight on the Hirobase login; otherwise the HiroJet marketing home renders.
const RootLanding = () => {
  const dest = rootRedirectPath();
  return dest ? <Navigate to={dest} replace /> : <HomePage />;
};

import '@/App.css';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const VIEW_AS_KEY = 'hj_view_as';
const VIEW_AS_ROLES = ['admin', 'manager', 'recruiter', 'bde'];

// Build the "effective" user an admin sees while previewing another role.
// Overrides the role AND strips every elevation flag so the persona renders
// as a vanilla member of that role. Purely client-side; the real login/token
// stays admin (data is not re-scoped server-side — UI/UX preview only).
//
// STRIPPED FLAGS (bug fix, 2026-02): previously only `can_*` was reset, so
// `is_superadmin` bled through and impersonators still saw the Platform
// nav section, admin-only route gates, etc.
const ELEVATION_FLAGS = [
  'is_superadmin', 'is_super_admin', 'is_owner', 'is_platform_admin',
  'is_staff', 'is_root',
];
const buildEffectiveUser = (realUser, viewAsRole) => {
  if (!realUser || realUser.role !== 'admin') return realUser;
  if (!viewAsRole || viewAsRole === 'admin') return realUser;
  const eff = { ...realUser, role: viewAsRole, _viewAs: true, _realRole: realUser.role };
  Object.keys(eff).forEach((k) => {
    if (k.startsWith('can_')) eff[k] = false;
  });
  ELEVATION_FLAGS.forEach((k) => {
    if (k in eff) eff[k] = false;
  });
  return eff;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [featureFlags, setFeatureFlags] = useState({});
  const [adminGrants, setAdminGrants] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRoleState] = useState(() => localStorage.getItem(VIEW_AS_KEY) || null);

  const setViewAs = useCallback((role) => {
    if (!role || role === 'admin' || !VIEW_AS_ROLES.includes(role)) {
      localStorage.removeItem(VIEW_AS_KEY);
      setViewAsRoleState(null);
    } else {
      localStorage.setItem(VIEW_AS_KEY, role);
      setViewAsRoleState(role);
    }
  }, []);

  useEffect(() => {
    const p = window.location.pathname;
    const isProtected = p.startsWith('/app') || p.startsWith('/w/') || p === '/login' || p === '/teamlogin' || p === '/forgot-password';
    if (!isProtected) {
      // Skip auth check on marketing pages — avoids unnecessary 401
      setLoading(false);
      return;
    }
    loadBootstrap()
      .then(data => {
        // ONE request for the whole shell (2026-06), shared with Layout via the
        // React Query cache so the chrome is never fetched twice.
        setUser(data.user);
        setFeatureFlags(data.feature_flags || {});
        setAdminGrants(data.admin_grants || {});
      })
      .catch(() => setUser(null))
      .finally(() => {
        setLoading(false);
        bootSplashAuthReady();
      });
  }, []);

  const login = useCallback(async (email, password, force = false) => {
    const res = await api.post('/auth/login', { email, password, force });
    // Cookie is set automatically by the response. User data is in res.data.user
    if (res.data.user) {
      setViewAs(null);
      setUser(res.data.user);
      loadBootstrap()
        .then(d => {
          setFeatureFlags(d.feature_flags || {});
          setAdminGrants(d.admin_grants || {});
        })
        .catch(() => setFeatureFlags({}));
    }
    return res.data;
  }, [setViewAs]);

  const loginWithGoogle = useCallback((userData) => {
    // Cookie was already set by the backend response. Just update React state.
    setViewAs(null);
    setUser(userData);
    loadBootstrap()
      .then(d => {
        setFeatureFlags(d.feature_flags || {});
        setAdminGrants(d.admin_grants || {});
      })
      .catch(() => setFeatureFlags({}));
  }, [setViewAs]);

  const logout = useCallback(async () => {
    const wasExternal = !!(user?.is_external || user?.role === 'external');
    try {
      await api.post('/auth/logout');
    } catch {
      // Best-effort — even if server call fails, clear local state
    }
    // Submission drafts hold a candidate's CTC, offer details and notes for up
    // to 7 days. On a shared machine they must not outlive the session.
    clearAllSubmissionDrafts();
    // `hj_active_workspace` is a CLIENT-PORTAL breadcrumb written by
    // WorkspaceLayout, and `signedOutRedirect()` below reads it to decide which
    // door to send a signed-out visitor to. It was never cleared, so once a
    // team member had opened any client workspace in that browser, every
    // subsequent logout bounced them to HiroBase (the client login) instead of
    // the HiroJet team login — the guard fires before Layout's explicit
    // navigate('/teamlogin') can land. Internal staff drop the breadcrumb on
    // the way out; clients keep it so they still land on their own door.
    // (owner, 5 Sep 2026)
    if (!wasExternal) {
      try { localStorage.removeItem('hj_active_workspace'); } catch { /* private mode */ }
    }
    setViewAs(null);
    setUser(null);
    setAdminGrants({});
  }, [setViewAs, user]);

  const effectiveUser = useMemo(() => buildEffectiveUser(user, viewAsRole), [user, viewAsRole]);
  // Suppress admin-grants while previewing another role so the persona doesn't
  // inherit the admin's Platform/BDE overrides.
  const effectiveGrants = useMemo(
    () => (viewAsRole && viewAsRole !== 'admin' ? {} : adminGrants),
    [adminGrants, viewAsRole]
  );

  if (loading) {
    // Splash screen in index.html already covers the initial load state.
    // Return an invisible placeholder to avoid double-loader overlap.
    return null;
  }

  return (
    <AuthContext.Provider value={{ user: effectiveUser, realUser: user, viewAsRole, setViewAs, login, loginWithGoogle, logout, setUser, featureFlags, adminGrants: effectiveGrants }}>
      {children}
    </AuthContext.Provider>
  );
};

// Where to send someone with no internal session. A signed-in CLIENT has no
// internal `user`, so the old blanket fallback put them on /teamlogin — our
// internal, HiroJet-branded login. `hj_active_workspace` is written by
// WorkspaceLayout on mount, so anyone who has ever been inside a workspace goes
// back to their own door instead. Used by every internal guard.
const signedOutRedirect = () => {
  try {
    return localStorage.getItem('hj_active_workspace') ? '/workspace-login' : '/teamlogin';
  } catch { return '/teamlogin'; }
};

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to={signedOutRedirect()} replace />;
  // Isolation guard: EXTERNAL (client/agency) users must never enter the internal
  // ATS shell — they belong only to their workspace. Bounce them to client login.
  if (user.is_external || user.role === 'external') return <Navigate to="/workspace-login" replace />;
  return children;
};

const NoAccessRedirect = () => {
  useEffect(() => { toast.error("You don't have access to that page.", { id: 'no-access' }); }, []);
  return <Navigate to="/app/dashboard" replace />;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/teamlogin" replace />;
  if (user.role !== 'admin') return <NoAccessRedirect />;
  return children;
};

// Admin or Director only
const DirectorUpRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/teamlogin" replace />;
  if (user.role !== 'admin' && user.role !== 'director') return <NoAccessRedirect />;
  return children;
};

// Allows both admin and manager roles
const AdminOrManagerRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/teamlogin" replace />;
  if (!['admin', 'director', 'manager'].includes(user.role)) return <NoAccessRedirect />;
  return children;
};

// Website Settings / CMS route gate.
// Access policy: admin (always) · users with can_manage_cms=true (per-user grant)
// · director/manager ONLY when workspace toggle manager_cms_access is enabled.
const CmsManagerRoute = ({ children }) => {
  const { user, featureFlags } = useAuth();
  if (!user) return <Navigate to="/teamlogin" replace />;
  if (user.role === 'admin') return children;
  if (user.can_manage_cms || featureFlags?.can_manage_cms) return children;
  if (['director', 'manager'].includes(user.role) && featureFlags?.manager_cms_access) return children;
  return <NoAccessRedirect />;
};

const BdeRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'bde') return <Navigate to="/app/bd-dashboard" replace />;
  return <Navigate to="/app/dashboard" replace />;
};

// Wraps the recruiting screens a BDE must never open. Business Development is
// a Director+ line (5 Sep 2026) — a Recruiting Manager is recruiting-only and
// keeps every recruiting screen, including the Talent Hub.
const NoRecruiting = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'bde') return <Navigate to="/app/bd-dashboard" replace />;
  return children;
};

// The kanban board is a manager+ tool: a recruiter cannot move a card (the
// stage endpoint is manager/director/admin only), so for them it was a wide
// read-only wall. They get the submissions LIST for that role instead, which
// answers the question they actually have. (Owner, 6 Jun 2026)
const BoardOrList = ({ children }) => {
  const { user } = useAuth();
  const { jobId } = useParams();
  if (user?.role === 'recruiter') {
    return <Navigate to={`/app/submissions?job=${jobId}`} replace />;
  }
  return children;
};

// Wraps routes that BDE users should NOT access
const NoBde = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'bde') return <Navigate to="/app/bd-dashboard" replace />;
  return children;
};

// Admin-only route guard (with optional feature flag override)
const AdminOnly = ({ children, featureFlag }) => {
  const { user } = useAuth();
  const [allowed, setAllowed] = useState(user?.role === 'admin');
  useEffect(() => {
    if (user?.role === 'admin') { setAllowed(true); return; }
    // Check user-level permission (e.g., can_view_sourcing_activity)
    if (featureFlag && user?.[`can_view_${featureFlag.replace('_visible', '')}`]) { setAllowed(true); return; }
    if (!featureFlag) { setAllowed(false); return; }
    api.getCached('/settings/feature-flags', 60000)
      .then(r => setAllowed(!!(r.data || {})[featureFlag]))
      .catch(() => setAllowed(false));
  }, [user, featureFlag]);
  if (allowed) return children;
  return <Navigate to="/app/dashboard" replace />;
};

// BDE + Director + Admin route guard
const BdeManagerAdmin = ({ children }) => {
  const { user } = useAuth();
  // Business Dev is for BDEs, Directors and Admins. Only a Director+ owns BDEs
  // (owner's rule, 5 Sep 2026) — Recruiting Managers have no BD access.
  if (hasBdAccess(user)) return children;
  return <Navigate to="/app/dashboard" replace />;
};

// Superadmin-only (platform console). Checks the platform-level flag, NOT a
// role — but ALSO honors an admin-granted `platform_settings` capability so
// specific Directors can be given Platform Console access without promotion.
// Superadmin-only (platform console). Checks the platform-level `is_superadmin`
// flag AND honors an admin-granted `platform_settings` capability so specific
// Directors can be given Platform Console access without promotion.
const SuperadminRoute = ({ children }) => {
  const { user, adminGrants } = useAuth();
  if (!user) return <Navigate to={signedOutRedirect()} replace />;
  if (user.is_superadmin) return children;
  if (adminGrants?.platform_settings) return children;
  return <Navigate to="/app/dashboard" replace />;
};

// Check for google_token or session_id synchronously during render - BEFORE ProtectedRoute runs
const AppRouter = () => {
  const location = useLocation();

  // PWA identity follows the surface: a client installing their workspace gets
  // HiroBase, everyone else gets HiroJet. Chrome re-reads <link rel="manifest">
  // on navigation, so swapping the href is enough — no second build, no second
  // service worker. If a real HiroBase icon set ever lands, only
  // public/manifest-hirobase.json changes.
  useEffect(() => {
    const el = document.querySelector('link[rel="manifest"]');
    if (!el) return;
    const clientSurface = /^\/(w\/|workspace-|hirobase|client\/login)/.test(location.pathname);
    const href = clientSurface ? '/manifest-hirobase.json' : '/manifest.json';
    if (!el.getAttribute('href')?.endsWith(href)) el.setAttribute('href', href);
  }, [location.pathname]);
  
  // Detect google_token or session_id in URL fragment synchronously (prevents race conditions)
  if (location.hash?.includes('google_token=') || location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* ============== PUBLIC MARKETING PAGES ============== */}
      <Route path="/sitemap.xml" element={<SitemapRedirect />} />
      {/* Every public marketing route sits inside MarketingScope: the site keeps
          its own violet brand, but needs its text classes scoped away from the
          ATS light theme (see `.mkt-scope` in index.css). */}
      <Route element={<MarketingScope />}>
        <Route path="/" element={<RootLanding />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:id" element={<BlogDetailPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/careers/jobs/:jobId" element={<JobDetailPage />} />
        <Route path="/jobs" element={<OpenPositionsPage />} />
        <Route path="/open-positions" element={<OpenPositionsPage />} />
        <Route path="/salary-calculator" element={<SalaryCalculatorPage />} />
        <Route path="/bd-guide" element={<BdGuidePage />} />
        <Route path="/ats-guide" element={<AtsGuidePage />} />
        <Route path="/hire/:slug" element={<HireLandingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy/my-data" element={<MyDataPage />} />
      </Route>
      <Route path="/recruiter-apply/:token" element={<RecruiterApplyPage />} />
      <Route path="/upload-resume/:token" element={<ResumeUploadPage />} />
      <Route path="/ai-confirm/:token" element={<AiScreenConfirmPage />} />
      <Route path="/availability/:token" element={<AvailabilityPage />} />
      {/* HiroJet meeting links — a person's own public booking page (26 Jun 2026) */}
      <Route path="/book/:slug" element={<BookingPage />} />
      <Route path="/book/:slug/:typeId" element={<BookingPage />} />
      <Route path="/booking/manage/:token" element={<BookingManagePage />} />
      <Route path="/pick-slot/:token" element={<CandidateSlotPage />} />
      <Route path="/interview-feedback/:token" element={<InterviewFeedbackPage />} />
      <Route path="/interview-confirm/:token" element={<InterviewConfirmPage />} />
      <Route path="/fitment/:token" element={<FitmentFormPage />} />

      {/* ============== DPDP / COMPLIANCE PUBLIC PAGES ============== */}
      
      {/* ============== PUBLIC AI RECRUITER PLATFORM (hidden) ============== */}
      <Route path="/platform/ai-recruiter" element={<PublicAiRecruiterPage />} />

      {/* ============== PUBLIC SHARED DASHBOARD LINKS ============== */}
      <Route path="/shared/api-usage/:token" element={<ApiUsageSharedPage />} />
      
      {/* ============== CLIENT PORTAL ============== */}
      <Route path="/client/login" element={<Navigate to="/workspace-login" replace />} />
      
      {/* ============== TEAM AUTH PAGES ============== */}
      <Route path="/teamlogin" element={<LoginPage />} />
      <Route path="/login" element={<Navigate to="/teamlogin" replace />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* ============== CLIENT ATS — WORKSPACE INVITE ACCEPT (public) ============== */}
      <Route path="/workspace-invite/:token" element={<WorkspaceInviteAcceptPage />} />

      {/* ============== CLIENT ATS — WORKSPACE (TENANT) APP ============== */}
      <Route path="/workspace-login" element={<WsClientLoginPage />} />
      <Route path="/workspace-forgot-password" element={<WsForgotPasswordPage />} />
      <Route path="/workspace-reset/:token" element={<WsResetPasswordPage />} />
      <Route path="/hirobase" element={<HirobaseMarketingPage />} />
      <Route path="/schedule/:token" element={<WsPublicSchedulePage />} />
      <Route path="/w/:slug" element={<WorkspaceLayout />}>
        <Route index element={<WsWorkspaceHome />} />
        <Route path="jobs" element={<WsJobsPage />} />
        <Route path="jobs/:jobId" element={<WsJobBoardPage />} />
        <Route path="jobs/:jobId/scorecard-template" element={<WsScorecardTemplatePage />} />
        <Route path="candidates" element={<WsCandidatesPage />} />
        <Route path="candidates/:candId" element={<WsCandidateProfilePage />} />
        <Route path="interviews" element={<WsInterviewsPage />} />
        <Route path="my-interviews" element={<WsMyInterviewsPage />} />
        <Route path="interviews/:ivId/scorecard" element={<WsScorecardFillPage />} />
        <Route path="members" element={<WsMembersPage />} />
        <Route path="usage" element={<WsUsagePage />} />
        <Route path="audit" element={<WsAuditPage />} />
        <Route path="settings" element={<WsSettingsPage />} />
        <Route path="agencies" element={<WsAgenciesPage />} />
        <Route path="agency/jobs" element={<WsAgencyJobsPage />} />
        <Route path="agency/submissions" element={<WsAgencySubmissionsPage />} />
        <Route path="agency/performance" element={<WsAgencyPerformancePage />} />
      </Route>
      
      {/* ============== PROTECTED ATS ROUTES ============== */}
      <Route path="/app" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<BdeRedirect />} />
        <Route path="dashboard" element={<NoRecruiting><DashboardPage /></NoRecruiting>} />
        <Route path="screening" element={<NoRecruiting><ScreeningQueuePage /></NoRecruiting>} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="jobs" element={<NoRecruiting><JobsPage /></NoRecruiting>} />
        <Route path="jobs/:jobId/pipeline" element={<NoRecruiting><BoardOrList><PipelinePage /></BoardOrList></NoRecruiting>} />
        <Route path="candidates" element={<NoRecruiting><TalentHubPage /></NoRecruiting>} />
        <Route path="submissions" element={<NoRecruiting><SubmissionsPage /></NoRecruiting>} />
        <Route path="candidates/:id" element={<NoRecruiting><CandidateDetailPage /></NoRecruiting>} />
        {/* HiroMatch (HiroApply + HiroPool) left Talent Hub on 6 Jun 2026 — its
            own nav row, because reviewing these is the recruiter's daily job. */}
        <Route path="hiromatch" element={<NoRecruiting><HiroMatchPage /></NoRecruiting>} />
        {/* Calendar became Interviews the same day; every old link redirects. */}
        <Route path="interviews" element={<NoBde><CombinedCalendarPage /></NoBde>} />
        <Route path="calendar" element={<Navigate to="/app/interviews" replace />} />
        <Route path="meetings" element={<MeetingsPage />} />
        <Route path="ai-recruiter" element={<AdminRoute><AIRecruiterPage /></AdminRoute>} />
        <Route path="ai-recruit/*" element={<AiRecruitPage />} />
        <Route path="bizx" element={<DirectorUpRoute><BizXPage /></DirectorUpRoute>} />
        <Route path="ai-calling" element={<NoBde><AiCallCampaignsPage /></NoBde>} />
        <Route path="ai-autodialer" element={<AdminRoute><AiAutodialerPage /></AdminRoute>} />
        <Route path="outreach" element={<AdminRoute><OutreachPage /></AdminRoute>} />
        <Route path="owner-dashboard" element={<AdminRoute><OwnerDashboardPage /></AdminRoute>} />
        <Route path="whats-new" element={<WhatsNewPage />} />
        <Route path="scoring-engine" element={<AdminRoute><ScoringEnginePage /></AdminRoute>} />
        {/* Combined pages */}
        {/* BDEs work accounts too — the client list is already scoped per user
            by get_visible_client_ids on the backend. */}
        <Route path="clients" element={<CombinedClientsPage />} />
        <Route path="clients/:id" element={<ClientDetailPage />} />
        <Route path="my-clients" element={<MyClientsPage />} />
        {/* Folded into Team Management (iter_518) — old link keeps working. */}
        <Route path="manager-performance" element={<Navigate to="/app/team?tab=managers" replace />} />
        <Route path="email-logs" element={<EmailLogsPage />} />
        <Route path="reports" element={<NoBde><CombinedReportsPage /></NoBde>} />
        <Route path="team" element={<NoBde><CombinedTeamPage /></NoBde>} />
        {/* People left Performance on 5 Sep 2026 — accounts, roles, reporting
            lines and the org chart are management, not performance. A manager
            sees their own reporting line only (backend scopes GET /users). */}
        <Route path="people" element={<AdminOrManagerRoute><UserManagementPage /></AdminOrManagerRoute>} />
        <Route path="learning" element={<NoBde><CombinedLearningPage /></NoBde>} />
        <Route path="settings" element={<AdminRoute><CombinedSettingsPage /></AdminRoute>} />
        <Route path="admin-tools" element={<AdminRoute><CombinedAdminToolsPage /></AdminRoute>} />
        <Route path="website" element={<CmsManagerRoute><SeoControlPage /></CmsManagerRoute>} />
        <Route path="business-dev" element={<BdeManagerAdmin><BusinessDevPage /></BdeManagerAdmin>} />
        {/* A BDE's Dashboard. Directors/admins may open it too — they get the
            BD line they own on it instead of only their own book. */}
        <Route path="bd-dashboard" element={<BdeManagerAdmin><BdDashboardPage /></BdeManagerAdmin>} />
        <Route path="marketing-analytics" element={<AdminOnly><MarketingAnalyticsPage /></AdminOnly>} />
        <Route path="billing" element={<AdminRoute><BillingPage /></AdminRoute>} />
        <Route path="product-analytics" element={<AdminRoute><ProductAnalyticsPage /></AdminRoute>} />
        {/* iter_524: Sourcing Activity now shows the LIVE in-app search log. The
            extension-fed screen it replaced is kept at .../extension-archive —
            dormant, not deleted, waiting on the rebuilt extension. */}
        <Route path="sourcing-activity" element={<AdminOrManagerRoute><SearchActivityPage /></AdminOrManagerRoute>} />
        {/* Are the review queues dealing, is the work being reviewed, and was
            anyone told? Managers see their own reporting line. */}
        <Route path="queue-health" element={<AdminOrManagerRoute><QueueHealthPage /></AdminOrManagerRoute>} />
        {/* Control Centre: every setting, explained, in one place. The fourteen
            admin sidebar rows collapse into this — every old address still
            works, and every dedicated screen is linked from its card. */}
        <Route path="control-centre" element={<DirectorUpRoute><ControlCentrePage /></DirectorUpRoute>} />
        <Route path="control-center" element={<Navigate to="/app/control-centre" replace />} />
        <Route path="sourcing-activity/extension-archive" element={<AdminRoute><SourcingActivityPage /></AdminRoute>} />
        {/* DPDP / Compliance — admin-only */}
        <Route path="admin/dsar" element={<AdminRoute><DSARQueuePage /></AdminRoute>} />
        <Route path="admin/compliance" element={<AdminRoute><CompliancePage /></AdminRoute>} />
        <Route path="admin/security" element={<AdminRoute><SecretsHealthPage /></AdminRoute>} />
        <Route path="admin/policies/internal-data-handling" element={<AdminRoute><InternalDataHandlingPage /></AdminRoute>} />
        <Route path="admin/data-hygiene" element={<AdminRoute><DataHygienePage /></AdminRoute>} />
        {/* Read-only census. Deliberately a separate screen from Data Hygiene:
            one reports, the other repairs. */}
        <Route path="admin/data-health" element={<AdminRoute><DataHealthPage /></AdminRoute>} />
        <Route path="admin/salary-repair" element={<AdminRoute><SalaryRepairPage /></AdminRoute>} />
        <Route path="admin/share-grants" element={<AdminRoute><AdminShareGrantsPage /></AdminRoute>} />
        <Route path="admin/bd-outreach-health" element={<AdminRoute><BDOutreachHealthPage /></AdminRoute>} />
        <Route path="admin/release-health" element={<AdminRoute><RegressionHealthPage /></AdminRoute>} />
        <Route path="admin/contact-reveal" element={<AdminOrManagerRoute><ContactRevealAdminPage /></AdminOrManagerRoute>} />
        <Route path="admin/email-routing" element={<AdminRoute><EmailRoutingPage /></AdminRoute>} />
        <Route path="admin/admin-only" element={<AdminRoute><AdminOnlyPage /></AdminRoute>} />
        {/* 1 Sep 2026: this route mounted the SAME component as
            /app/sourcing-activity — two nav entries, one screen. The link keeps
            working; it now lands on the canonical one. Nothing deleted. */}
        <Route path="admin/search-activity" element={<Navigate to="/app/sourcing-activity" replace />} />
        <Route path="admin/api-usage" element={<AdminRoute><ApiUsagePage /></AdminRoute>} />
        <Route path="admin" element={<AdminOrManagerRoute><ComplianceHubPage /></AdminOrManagerRoute>} />
        {/* Client ATS — Platform Console (superadmin, multi-tenant) */}
        <Route path="platform/workspaces" element={<SuperadminRoute><PlatformWorkspacesPage /></SuperadminRoute>} />
        <Route path="platform/workspaces/:id" element={<SuperadminRoute><PlatformWorkspaceDetailPage /></SuperadminRoute>} />
        <Route path="platform/usage" element={<SuperadminRoute><PlatformUsagePage /></SuperadminRoute>} />
        <Route path="platform/audit" element={<SuperadminRoute><PlatformAuditPage /></SuperadminRoute>} />
        <Route path="platform/hirobase-leads" element={<SuperadminRoute><PlatformHirobaseLeadsPage /></SuperadminRoute>} />
        {/* Standalone pages still needed */}
        <Route path="settings/change-password" element={<ChangePasswordPage />} />
        <Route path="profile" element={<ProfilePage />} />
        {/* Old routes → redirects to combined pages */}
        <Route path="analytics" element={<Navigate to="/app/reports?tab=analytics" replace />} />
        <Route path="client-portal" element={<Navigate to="/app/clients?tab=portal" replace />} />
        <Route path="settings/daily-targets" element={<Navigate to="/app/team?tab=targets" replace />} />
        <Route path="settings/team" element={<Navigate to="/app/people" replace />} />
        <Route path="team-directory" element={<Navigate to="/app/people" replace />} />
        <Route path="training" element={<Navigate to="/app/learning?tab=academy" replace />} />
        <Route path="job-research" element={<Navigate to="/app/learning?tab=research" replace />} />
        <Route path="help" element={<Navigate to="/app/learning?tab=help" replace />} />
        <Route path="settings/users" element={<Navigate to="/app/people" replace />} />
        <Route path="approvals" element={<Navigate to="/app/dashboard?section=approvals" replace />} />
        <Route path="control-settings" element={<Navigate to="/app/settings?tab=ats" replace />} />
        <Route path="website-control" element={<Navigate to="/app/settings?tab=website" replace />} />
        <Route path="security" element={<Navigate to="/app/admin-tools?tab=security" replace />} />
        <Route path="bulk-import" element={<Navigate to="/app/admin-tools?tab=bulk-import" replace />} />
        <Route path="settings/content" element={<Navigate to="/app/settings?tab=ats" replace />} />
        <Route path="control-center" element={<Navigate to="/app/admin-tools?tab=security" replace />} />
        <Route path="ats-settings" element={<Navigate to="/app/settings?tab=ats" replace />} />
        <Route path="resume-search" element={<Navigate to="/app/candidates" replace />} />
      </Route>
      
      {/* Legacy routes - redirect to new structure */}
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/candidates" element={<Navigate to="/app/candidates" replace />} />
      <Route path="/clients" element={<Navigate to="/app/clients" replace />} />
      <Route path="/interviews" element={<Navigate to="/app/interviews" replace />} />
      <Route path="/analytics" element={<Navigate to="/app/reports?tab=analytics" replace />} />
      <Route path="/reports" element={<Navigate to="/app/reports" replace />} />
      <Route path="/settings/*" element={<Navigate to="/app/settings" replace />} />
      
      {/* ============== PUBLIC SHARE ROUTES ============== */}
      <Route path="/share/:token" element={<SharedCandidatesPage />} />
      <Route path="/ct/:token" element={<ClientTrackerPublicPage />} />

      {/* ============== 404 CATCH-ALL ============== */}
      {/* Old WordPress URL redirects */}
      <Route path="/hire-developers-efficiently-with-hirojet/*" element={<Navigate to="/blog/1" replace />} />
      <Route path="/hiring-software-developer-usa/*" element={<Navigate to="/hire/backend-engineers-in-san-francisco" replace />} />
      <Route path="/hire-backend-developer-2025-guide-usa/*" element={<Navigate to="/hire/backend-engineers-in-new-york" replace />} />
      <Route path="/hire-software-engineers-hirojet/*" element={<Navigate to="/services" replace />} />
      <Route path="/hire-frontend-developers-usa/*" element={<Navigate to="/hire/frontend-engineers-in-san-francisco" replace />} />
      <Route path="/hire-product-designer-2025-guide/*" element={<Navigate to="/services" replace />} />
      <Route path="/hire-ui-developers-from-india/*" element={<Navigate to="/hire/frontend-engineers-in-bangalore" replace />} />
      <Route path="/how-to-hire-product-manager-2025/*" element={<Navigate to="/hire/product-managers-in-bangalore" replace />} />
      <Route path="/privacy-policy/*" element={<Navigate to="/about" replace />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
};

import { ThemeProvider } from '@/contexts/ThemeContext';
import { HelmetProvider } from 'react-helmet-async';
import { SeoProvider } from '@/components/SEO';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ConfirmDialogHost } from '@/components/ConfirmDialog';

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  // Splash screen is now handled by AuthProvider after auth resolves

  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={googleClientId}>
        <HelmetProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <AuthProvider>
                <ThemeProvider>
                  <SeoProvider>
                    <Toaster theme="dark" position="top-right" richColors />
                    <ConfirmDialogHost />
                    <TopLoadingBar />
                    <StartingBanner />
                    <AppRouter />
                  </SeoProvider>
                </ThemeProvider>
              </AuthProvider>
            </ErrorBoundary>
          </BrowserRouter>
        </HelmetProvider>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
}

export default App;
